import { useState, useEffect } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { useSwipeable } from 'react-swipeable';
import { isGameFinished } from '../utils/constants';
import { isPotentialOpener, getPreviousProbablePitcher } from '../utils/mlbBullpen';

const MobileAnalyticsDataTable = ({
  analyticsByGameId,
  analyticsLoadingGameIds,
  gameTime,
  gameStatus,
  gameDate,
  lastUpdate,
  game,
  formatPlayerName,
  getTeamLogoUrl,
  getBookLogoUrl,
  selectedMarket,
  selectedMarketName,
  bookmakers,
  isLoading,
  availableMarkets,
  onMarketSelection,
  showTiers,
  onTierToggle,
  masterTierFilter,
  onPlayerClick,
  onOddsClick,
  isCollapsed = false,
  onCollapseToggle
}) => {
  // Analytics view states
  const [analyticsType, setAnalyticsType] = useState('matchups');
  const [selectedYear, setSelectedYear] = useState('alltime'); // Same as desktop
  const [performanceView, setPerformanceView] = useState('last7');
  const [pitcherHandFilter, setPitcherHandFilter] = useState('vsAll');
  const [pitchesView, setPitchesView] = useState('main');
  const [pitchesDisplayMode, setPitchesDisplayMode] = useState('all');
  const [awayPitchIndex, setAwayPitchIndex] = useState(0);
  const [homePitchIndex, setHomePitchIndex] = useState(0);
  // Separate indices for splits mode
  const [awayRHBPitchIndex, setAwayRHBPitchIndex] = useState(0);
  const [awayLHBPitchIndex, setAwayLHBPitchIndex] = useState(0);
  const [homeRHBPitchIndex, setHomeRHBPitchIndex] = useState(0);
  const [homeLHBPitchIndex, setHomeLHBPitchIndex] = useState(0);

  // Debug: Log if onPlayerClick is available
  useEffect(() => {
    console.log('MobileAnalyticsDataTable - onPlayerClick available:', !!onPlayerClick);
  }, [onPlayerClick]);

  const safeGame = game || { away: 'TBD', home: 'TBD', gameIDmlb: 'loading' };

  // Local data state like the original component
  const [localData, setLocalData] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);

  // Reset pitch indexes when pitches settings change
  useEffect(() => {
    setAwayPitchIndex(0);
    setHomePitchIndex(0);
    setAwayRHBPitchIndex(0);
    setAwayLHBPitchIndex(0);
    setHomeRHBPitchIndex(0);
    setHomeLHBPitchIndex(0);
  }, [pitchesDisplayMode, pitcherHandFilter, pitchesView]);

  // Fetch analytics data when analytics type changes (same as original)
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!safeGame.gameIDmlb || safeGame.gameIDmlb === 'loading') return;

      // Check if we already have this data
      const existingData = analyticsByGameId[safeGame.gameIDmlb]?.[analyticsType];
      if (existingData) {
        setLocalData(existingData);
        return;
      }

      setLocalLoading(true);
      try {
        const endpoint = `/api/analytics/game/${safeGame.gameIDmlb}/${analyticsType === 'pitches' ? 'pitches-all' : analyticsType}`;
        const response = await fetch(endpoint);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const rawData = await response.json();
        console.log('Raw API response:', rawData);

        // Process the data based on analytics type (same as original)
        let processedData = rawData;
        if (analyticsType === 'performance' && pitcherHandFilter !== 'vsAll') {
          // Apply pitcher hand filter if needed
          processedData = rawData; // For now, keep as is
        }

        setLocalData(processedData);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLocalLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [analyticsType, safeGame.gameIDmlb, analyticsByGameId, pitcherHandFilter]);

  // Debug logging
  console.log('MobileAnalyticsDataTable Debug:', {
    gameId: safeGame.gameIDmlb,
    analyticsType,
    localData,
    localLoading
  });

  // Mobile carousel state - show 3 stats at a time
  const [mobileStatIndex, setMobileStatIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const statsPerPage = 3;

  // Format pitch names to shorter versions with usage percentage
  const formatPitchName = (pitchType, pitchData) => {
    // Shorten common pitch names
    const shortNames = {
      'Four-Seam Fastball': '4-Seam FB',
      'Two-Seam Fastball': '2-Seam FB',
      'Curveball': 'Curveball',
      'Slider': 'Slider',
      'Changeup': 'Changeup',
      'Sinker': 'Sinker',
      'Cutter': 'Cutter',
      'Sweeper': 'Sweeper',
      'Knuckleball': 'Knuckleball'
    };

    const shortName = shortNames[pitchType] || pitchType;
    const usage = pitchData?.usage;

    return usage ? `${shortName} (${usage}%)` : shortName;
  };

  // Process analytics data for mobile display
  const processAnalyticsData = () => {
    console.log('Processing analytics data:', { localData, analyticsType });
    if (!localData) {
      console.log('No localData available');
      return { columns: [], awayBatters: [], homeBatters: [] };
    }

    console.log('LocalData structure:', Object.keys(localData));

    // For matchups data - data is organized by year, need to process by team
    if (analyticsType === 'matchups') {
      const columns = [
        { key: 'ab', label: 'AB', name: 'At Bats' },
        { key: 'avg', label: 'AVG', name: 'Average' },
        { key: 'slg', label: 'SLG', name: 'Slugging Percentage' }
      ];

      // Use same data processing as desktop - get data for selected year
      let yearData = [];
      if (typeof localData === 'object' && !Array.isArray(localData)) {
        // New format with years as keys (same as desktop)
        yearData = localData[selectedYear] || [];
      } else {
        // Fallback for old format
        yearData = Array.isArray(localData) ? localData : [];
      }
      console.log('Year data:', yearData.length, 'players');

      // Separate by team - need to get team names from game data
      const awayTeam = safeGame.away;
      const homeTeam = safeGame.home;

      // Transform data to use consistent field names
      const transformMatchupPlayer = (player) => {
        return {
          ...player,
          ab: player.totalAtBats || player.atBats || player.ab || 0,
          avg: player.battingAvg || player.avg || 0,
          slg: player.slg || player.sluggingPct || 0
        };
      };

      const awayBatters = yearData
        .filter(player =>
          player.batterTeam === awayTeam ||
          player.batterTeam === safeGame.awayTeamFull ||
          player.batterTeam?.toLowerCase().includes(awayTeam?.toLowerCase())
        )
        .map(transformMatchupPlayer);

      const homeBatters = yearData
        .filter(player =>
          player.batterTeam === homeTeam ||
          player.batterTeam === safeGame.homeTeamFull ||
          player.batterTeam?.toLowerCase().includes(homeTeam?.toLowerCase())
        )
        .map(transformMatchupPlayer);

      console.log('Matchups data separated by team:', {
        awayTeam,
        homeTeam,
        totalPlayers: yearData.length,
        awayBatters: awayBatters.length,
        homeBatters: homeBatters.length,
        sampleAwayPlayer: awayBatters[0],
        sampleHomePlayer: homeBatters[0],
        availableFields: awayBatters[0] ? Object.keys(awayBatters[0]) : []
      });

      return {
        columns,
        awayBatters,
        homeBatters
      };
    }

    // For performance (Last X) mode
    if (analyticsType === 'performance') {
      console.log('Performance mode - processing data');

      // Handle different API response structures for performance
      let yearData = [];
      if (localData.performance && Array.isArray(localData.performance)) {
        // Fresh API response: {success: true, performance: [...]}
        yearData = localData.performance;
        console.log('Performance mode - using direct performance array');
      } else if (typeof localData === 'object' && !Array.isArray(localData)) {
        // Cached response with years as keys
        yearData = localData[selectedYear] || [];
        console.log('Performance mode - using year-based data');
      } else {
        yearData = Array.isArray(localData) ? localData : [];
        console.log('Performance mode - using fallback array');
      }
      console.log('Performance year data:', yearData.length, 'players');

      const columns = [
        { key: 'ab', label: 'AB', name: 'At Bats' },
        { key: 'avg', label: 'AVG', name: 'Average' },
        { key: 'slg', label: 'SLG', name: 'Slugging Percentage' }
      ];

      // Separate by team and transform performance data
      const awayTeam = safeGame.away;
      const homeTeam = safeGame.home;

      // Transform and filter the data
      const transformPerformancePlayer = (player) => {
        // Get the correct time period data
        let periodKey = 'last7Days'; // default
        if (performanceView === 'last7') periodKey = 'last7Days';
        else if (performanceView === 'last14') periodKey = 'last14Days';
        else if (performanceView === 'last30') periodKey = 'last30Days';

        // Get the correct pitcher hand data
        let handKey = 'vsAll'; // default
        if (pitcherHandFilter === 'vsRHP') handKey = 'vsRHP';
        else if (pitcherHandFilter === 'vsLHP') handKey = 'vsLHP';

        // Extract the nested performance data
        const performanceData = player.performance?.[periodKey]?.[handKey];

        if (!performanceData) {
          console.warn(`No performance data for ${player.batterName} - ${periodKey}/${handKey}`);
          return null;
        }

        // Return flattened player object with stats at top level
        return {
          ...player,
          ab: performanceData.atBats,
          avg: performanceData.battingAvg,
          slg: performanceData.sluggingPct,
          ops: performanceData.ops,
          // Keep original nested data for reference
          performanceData
        };
      };

      const awayBatters = yearData
        .filter(player =>
          player.batterTeam === awayTeam ||
          player.batterTeam === safeGame.awayTeamFull ||
          player.batterTeam?.toLowerCase().includes(awayTeam?.toLowerCase())
        )
        .map(transformPerformancePlayer)
        .filter(player => player !== null);

      const homeBatters = yearData
        .filter(player =>
          player.batterTeam === homeTeam ||
          player.batterTeam === safeGame.homeTeamFull ||
          player.batterTeam?.toLowerCase().includes(homeTeam?.toLowerCase())
        )
        .map(transformPerformancePlayer)
        .filter(player => player !== null);

      console.log('Performance data separated by team:', {
        awayTeam,
        homeTeam,
        totalPlayers: yearData.length,
        awayBatters: awayBatters.length,
        homeBatters: homeBatters.length,
        sampleAwayPlayer: awayBatters[0],
        sampleHomePlayer: homeBatters[0]
      });

      return {
        columns,
        awayBatters,
        homeBatters
      };
    }

    // For pitches mode, process pitcher data differently
    if (analyticsType === 'pitches') {
      console.log('Pitches mode - processing pitcher data');

      // Handle the new pitcher-based API response
      let pitchersData = [];
      if (localData.rawPitchersData && Array.isArray(localData.rawPitchersData)) {
        // Fresh API response with rawPitchersData structure (same as desktop)
        pitchersData = localData.rawPitchersData;
        console.log('Pitches mode - using rawPitchersData array');
      } else if (localData.pitchers && Array.isArray(localData.pitchers)) {
        // Legacy API response: {success: true, pitchers: [...]}
        pitchersData = localData.pitchers;
        console.log('Pitches mode - using direct pitchers array');
      } else if (typeof localData === 'object' && !Array.isArray(localData)) {
        // Fallback - maybe cached data has different structure
        const cachedData = localData[selectedYear] || [];
        console.log('Pitches mode - using cached data, length:', cachedData.length);

        // If cached data looks like old format, return simple fallback
        if (cachedData.length > 0 && cachedData[0].batterName) {
          console.log('Using old cached format');
          const awayBatters = cachedData.filter(player =>
            (player.batterTeam === safeGame.away || player.batterTeam === safeGame.awayTeamFull) && !player.isPitcher
          );
          const homeBatters = cachedData.filter(player =>
            (player.batterTeam === safeGame.home || player.batterTeam === safeGame.homeTeamFull) && !player.isPitcher
          );

          return {
            columns: [
              { key: 'avg', label: 'AVG', name: 'Average', pitchName: 'Mixed' },
              { key: 'slg', label: 'SLG', name: 'Slugging', pitchName: 'Mixed' },
              { key: 'hr', label: 'HR', name: 'Home Runs', pitchName: 'Mixed' }
            ],
            awayBatters,
            homeBatters
          };
        }
      }

      console.log('Found pitchers:', pitchersData.length);

      // Now process the batters data from the API
      const battersData = localData.rawBattersData || localData.batters || [];
      console.log('Found batters:', battersData.length);

      // Define team names for filtering
      const awayTeam = safeGame.away;
      const homeTeam = safeGame.home;

      // Get individual pitchers for team-specific pitch percentages
      const homePitcher = pitchersData.find(p => p.pitcherTeam === homeTeam);
      const awayPitcher = pitchersData.find(p => p.pitcherTeam === awayTeam);

      // Determine the correct pitch type key based on display mode
      let pitchTypeKey;
      if (pitchesDisplayMode === 'all') {
        pitchTypeKey = 'vsAll';
      } else if (pitchesDisplayMode === 'splits') {
        // In splits mode, we need to check the pitcher's throwing hand
        // For now, we'll use vsAll as we need to determine pitcher handedness
        pitchTypeKey = 'vsAll';
      } else {
        pitchTypeKey = pitcherHandFilter === 'vsRHP' ? 'vsRHP' : 'vsLHB';
      }

      // Get all unique pitch types from both pitchers
      const homePitchTypes = homePitcher?.top4Pitches?.[pitchTypeKey] || [];
      const awayPitchTypes = awayPitcher?.top4Pitches?.[pitchTypeKey] || [];

      // Create a combined list of unique pitch types
      const allPitchTypes = [];
      const seenPitchTypes = new Set();

      // Add home pitcher's pitches first
      homePitchTypes.forEach(pitch => {
        if (!seenPitchTypes.has(pitch.pitchType)) {
          allPitchTypes.push(pitch.pitchType);
          seenPitchTypes.add(pitch.pitchType);
        }
      });

      // Add away pitcher's pitches that aren't already included
      awayPitchTypes.forEach(pitch => {
        if (!seenPitchTypes.has(pitch.pitchType)) {
          allPitchTypes.push(pitch.pitchType);
          seenPitchTypes.add(pitch.pitchType);
        }
      });

      // Get team-specific pitch types
      // Find the opposing pitchers for each team
      const opposingHomePitcher = pitchersData.find(p => p.pitcherTeam === homeTeam);
      const opposingAwayPitcher = pitchersData.find(p => p.pitcherTeam === awayTeam);

      // Get available pitches for each team from their opposing pitcher
      let awayAvailablePitches, homeAvailablePitches;
      let awaySelectedPitchType, homeSelectedPitchType;
      let awayRHBSelectedPitchType, awayLHBSelectedPitchType, homeRHBSelectedPitchType, homeLHBSelectedPitchType;

      if (pitchesDisplayMode === 'splits') {
        // In splits mode, get RHB and LHB specific pitches
        const awayRHBAvailablePitches = opposingHomePitcher?.top4Pitches?.vsRHB?.map(p => p.pitchType) || [];
        const awayLHBAvailablePitches = opposingHomePitcher?.top4Pitches?.vsLHB?.map(p => p.pitchType) || [];
        const homeRHBAvailablePitches = opposingAwayPitcher?.top4Pitches?.vsRHB?.map(p => p.pitchType) || [];
        const homeLHBAvailablePitches = opposingAwayPitcher?.top4Pitches?.vsLHB?.map(p => p.pitchType) || [];

        // Use separate indices for each handedness
        const safeAwayRHBPitchIndex = Math.max(0, Math.min(awayRHBPitchIndex, awayRHBAvailablePitches.length - 1));
        const safeAwayLHBPitchIndex = Math.max(0, Math.min(awayLHBPitchIndex, awayLHBAvailablePitches.length - 1));
        const safeHomeRHBPitchIndex = Math.max(0, Math.min(homeRHBPitchIndex, homeRHBAvailablePitches.length - 1));
        const safeHomeLHBPitchIndex = Math.max(0, Math.min(homeLHBPitchIndex, homeLHBAvailablePitches.length - 1));

        awayRHBSelectedPitchType = awayRHBAvailablePitches[safeAwayRHBPitchIndex] || 'Four-Seam Fastball';
        awayLHBSelectedPitchType = awayLHBAvailablePitches[safeAwayLHBPitchIndex] || 'Four-Seam Fastball';
        homeRHBSelectedPitchType = homeRHBAvailablePitches[safeHomeRHBPitchIndex] || 'Four-Seam Fastball';
        homeLHBSelectedPitchType = homeLHBAvailablePitches[safeHomeLHBPitchIndex] || 'Four-Seam Fastball';

        // For backward compatibility, use RHB as default
        awaySelectedPitchType = awayRHBSelectedPitchType;
        homeSelectedPitchType = homeRHBSelectedPitchType;
      } else {
        // Regular mode
        awayAvailablePitches = opposingHomePitcher?.top4Pitches?.[pitchTypeKey]?.map(p => p.pitchType) || [];
        homeAvailablePitches = opposingAwayPitcher?.top4Pitches?.[pitchTypeKey]?.map(p => p.pitchType) || [];

        const safeAwayPitchIndex = Math.max(0, Math.min(awayPitchIndex, awayAvailablePitches.length - 1));
        const safeHomePitchIndex = Math.max(0, Math.min(homePitchIndex, homeAvailablePitches.length - 1));
        awaySelectedPitchType = awayAvailablePitches[safeAwayPitchIndex] || 'Four-Seam Fastball';
        homeSelectedPitchType = homeAvailablePitches[safeHomePitchIndex] || 'Four-Seam Fastball';
      }

      console.log('Team-specific pitch selection debug:', {
        away: {
          pitchIndex: awayPitchIndex,
          selectedPitchType: awaySelectedPitchType
        },
        home: {
          pitchIndex: homePitchIndex,
          selectedPitchType: homeSelectedPitchType
        }
      });

      console.log('Selected pitches:', {
        awaySelectedPitchType,
        homeSelectedPitchType,
        homePitcher: homePitcher?.pitcherName,
        awayPitcher: awayPitcher?.pitcherName
      });

      // Define columns based on pitchesView (main vs advanced) - moved here to be available for all return paths
      const pitchColumns = pitchesView === 'main' ? [
        { key: 'avg', label: 'AVG', name: 'Average', pitchName: 'Dynamic',
          pitchData: { homePitcher, awayPitcher, awaySelectedPitchType, homeSelectedPitchType,
            awayRHBSelectedPitchType, awayLHBSelectedPitchType,
            homeRHBSelectedPitchType, homeLHBSelectedPitchType, pitchTypeKey, pitchesDisplayMode } },
        { key: 'slg', label: 'SLG', name: 'Slugging', pitchName: 'Dynamic',
          pitchData: { homePitcher, awayPitcher, awaySelectedPitchType, homeSelectedPitchType,
            awayRHBSelectedPitchType, awayLHBSelectedPitchType,
            homeRHBSelectedPitchType, homeLHBSelectedPitchType, pitchTypeKey, pitchesDisplayMode } },
        { key: 'hr', label: 'HR', name: 'Home Runs', pitchName: 'Dynamic',
          pitchData: { homePitcher, awayPitcher, awaySelectedPitchType, homeSelectedPitchType,
            awayRHBSelectedPitchType, awayLHBSelectedPitchType,
            homeRHBSelectedPitchType, homeLHBSelectedPitchType, pitchTypeKey, pitchesDisplayMode } }
      ] : [
        { key: 'avg', label: 'AVG', name: 'Average', pitchName: 'Dynamic',
          pitchData: { homePitcher, awayPitcher, awaySelectedPitchType, homeSelectedPitchType,
            awayRHBSelectedPitchType, awayLHBSelectedPitchType,
            homeRHBSelectedPitchType, homeLHBSelectedPitchType, pitchTypeKey, pitchesDisplayMode } },
        { key: 'slg', label: 'SLG', name: 'Slugging', pitchName: 'Dynamic',
          pitchData: { homePitcher, awayPitcher, awaySelectedPitchType, homeSelectedPitchType,
            awayRHBSelectedPitchType, awayLHBSelectedPitchType,
            homeRHBSelectedPitchType, homeLHBSelectedPitchType, pitchTypeKey, pitchesDisplayMode } },
        { key: 'hr', label: 'HR', name: 'Home Runs', pitchName: 'Dynamic',
          pitchData: { homePitcher, awayPitcher, awaySelectedPitchType, homeSelectedPitchType,
            awayRHBSelectedPitchType, awayLHBSelectedPitchType,
            homeRHBSelectedPitchType, homeLHBSelectedPitchType, pitchTypeKey, pitchesDisplayMode } },
        { key: 'hhPercent', label: 'HH%', name: 'Hard Hit Percentage', pitchName: 'Dynamic',
          pitchData: { homePitcher, awayPitcher, awaySelectedPitchType, homeSelectedPitchType,
            awayRHBSelectedPitchType, awayLHBSelectedPitchType,
            homeRHBSelectedPitchType, homeLHBSelectedPitchType, pitchTypeKey, pitchesDisplayMode } },
        { key: 'barrelPercent', label: 'Barrel%', name: 'Barrel Percentage', pitchName: 'Dynamic',
          pitchData: { homePitcher, awayPitcher, awaySelectedPitchType, homeSelectedPitchType,
            awayRHBSelectedPitchType, awayLHBSelectedPitchType,
            homeRHBSelectedPitchType, homeLHBSelectedPitchType, pitchTypeKey, pitchesDisplayMode } },
        { key: 'avgXV', label: 'avgXV', name: 'Average Exit Velocity', pitchName: 'Dynamic',
          pitchData: { homePitcher, awayPitcher, awaySelectedPitchType, homeSelectedPitchType,
            awayRHBSelectedPitchType, awayLHBSelectedPitchType,
            homeRHBSelectedPitchType, homeLHBSelectedPitchType, pitchTypeKey, pitchesDisplayMode } }
      ];

      // Process batters similar to performance data but filter by pitch type
      const transformBatterForPitch = (batter, pitchType, opposingPitcher = null) => {
        let handKey = 'vsAll';

        if (pitchesDisplayMode === 'splits' && opposingPitcher) {
          // In splits mode, use the opposing pitcher's throwing hand to determine the key
          // If the pitcher throws right, batters face RHP; if left, batters face LHP
          const pitcherThrows = opposingPitcher.pitcherThrows || opposingPitcher.throws || '';
          console.log(`DEBUG transformBatterForPitch - Batter: ${batter.batterName} (${batter.batterBats}), Opposing Pitcher: ${opposingPitcher.pitcherName}, Throws: ${pitcherThrows}, Pitch Type: ${pitchType}`);

          if (pitcherThrows.toLowerCase().includes('right') || pitcherThrows === 'R') {
            handKey = 'vsRHP'; // Batters are facing a right-handed pitcher
          } else if (pitcherThrows.toLowerCase().includes('left') || pitcherThrows === 'L') {
            handKey = 'vsLHP'; // Batters are facing a left-handed pitcher
          }

          console.log(`DEBUG - Determined handKey: ${handKey}`);
        } else if (pitchesDisplayMode !== 'all') {
          // Use the pitcher hand filter if not in splits mode
          if (pitcherHandFilter === 'vsRHP') handKey = 'vsRHP';
          else if (pitcherHandFilter === 'vsLHP') handKey = 'vsLHP';
        }

        const pitchData = batter.allPitches?.[handKey]?.find(pitch =>
          pitch.pitchType === pitchType
        );


        if (!pitchData) {
          console.log(`No pitch data for ${batter.batterName} vs ${pitchType} (${handKey}) - showing zeros`);
          // Return batter with zero values like desktop does
          return {
            ...batter,
            // Main metrics - zeros when no data
            avg: 0,
            obp: null,
            slg: 0,
            ops: 0,
            hr: 0,
            atBats: 0, // 0 at-bats means we show dashes
            hits: 0,
            // Advanced metrics - zeros when no data
            hhPercent: 0,
            barrelPercent: 0,
            avgXV: 0,
            pitchData: null
          };
        }

        return {
          ...batter,
          // Main metrics
          avg: pitchData.battingAvg || 0,
          obp: null, // Not provided in pitch data
          slg: pitchData.sluggingPct || 0,
          ops: pitchData.ops || 0,
          hr: pitchData.homeRuns || 0,
          atBats: pitchData.atBats || 0,
          hits: pitchData.hits || 0,
          // Advanced metrics
          hhPercent: pitchData.hardHitRate || 0,
          barrelPercent: pitchData.barrelRate || 0,
          avgXV: pitchData.avgExitVelo || 0,
          pitchData
        };
      };

      let awayBatters, homeBatters;

      if (pitchesDisplayMode === 'splits') {
        // In splits mode, mimic desktop: ALL batters use same handKey based on opposing pitcher
        // Desktop determines pitcher handedness and ALL batters use that same key

        // Get batters by team
        const allAwayBatters = battersData
          .filter(batter =>
            batter.batterTeam === awayTeam ||
            batter.batterTeam === safeGame.awayTeamFull ||
            batter.batterTeam?.toLowerCase().includes(awayTeam?.toLowerCase())
          );

        const allHomeBatters = battersData
          .filter(batter =>
            batter.batterTeam === homeTeam ||
            batter.batterTeam === safeGame.homeTeamFull ||
            batter.batterTeam?.toLowerCase().includes(homeTeam?.toLowerCase())
          );

        // Separate by handedness for display grouping
        const awayRHBattersRaw = allAwayBatters.filter(b => b.batterBats === 'Right');
        const awayLHBattersRaw = allAwayBatters.filter(b => b.batterBats === 'Left');
        const homeRHBattersRaw = allHomeBatters.filter(b => b.batterBats === 'Right');
        const homeLHBattersRaw = allHomeBatters.filter(b => b.batterBats === 'Left');

        // Transform each group with the HANDEDNESS-SPECIFIC pitch type
        // Each section gets its own independent pitch type selection
        // Away batters face home pitcher
        const awayRHBatters = awayRHBattersRaw
          .map(batter => transformBatterForPitch(batter, awayRHBSelectedPitchType, homePitcher));
        const awayLHBatters = awayLHBattersRaw
          .map(batter => transformBatterForPitch(batter, awayLHBSelectedPitchType, homePitcher));

        // Home batters face away pitcher
        const homeRHBatters = homeRHBattersRaw
          .map(batter => transformBatterForPitch(batter, homeRHBSelectedPitchType, awayPitcher));
        const homeLHBatters = homeLHBattersRaw
          .map(batter => transformBatterForPitch(batter, homeLHBSelectedPitchType, awayPitcher));

        // Create pitcher stats rows for each handedness
        const createPitcherRow = (handType, pitcherData, selectedPitchType) => {
          const handKey = handType === 'RHB' ? 'vsRHB' : 'vsLHB';
          const pitchData = pitcherData?.top4Pitches?.[handKey]?.find(pitch =>
            pitch.pitchType === selectedPitchType
          );

          return {
            batterId: pitcherData?.pitcherId || `pitcher_${handType}`,
            batterName: `vs ${handType}`,
            batterTeam: pitcherData?.pitcherTeam,
            teamLogo: pitcherData?.teamLogo,
            isPitcher: true,
            isLabel: true,
            avg: pitchData?.battingAvg || 0,
            slg: pitchData?.sluggingPct || 0,
            hr: pitchData?.homeRuns || 0,
            ab: 0 // Pitchers don't have at-bats
          };
        };

        // Pitcher data already retrieved earlier (lines 371-372)

        // Away batters face home pitcher, use handedness-specific pitch types in splits mode
        const homePitcherRHBRow = createPitcherRow('RHB', homePitcher,
          pitchesDisplayMode === 'splits' ? awayRHBSelectedPitchType : awaySelectedPitchType);
        const homePitcherLHBRow = createPitcherRow('LHB', homePitcher,
          pitchesDisplayMode === 'splits' ? awayLHBSelectedPitchType : awaySelectedPitchType);
        // Home batters face away pitcher, use handedness-specific pitch types in splits mode
        const awayPitcherRHBRow = createPitcherRow('RHB', awayPitcher,
          pitchesDisplayMode === 'splits' ? homeRHBSelectedPitchType : homeSelectedPitchType);
        const awayPitcherLHBRow = createPitcherRow('LHB', awayPitcher,
          pitchesDisplayMode === 'splits' ? homeLHBSelectedPitchType : homeSelectedPitchType);

        // Keep RHB and LHB separate for splits mode
        // Away team sections
        const awayRHBSection = awayRHBatters.length > 0 ? [homePitcherRHBRow, ...awayRHBatters] : [];
        const awayLHBSection = awayLHBatters.length > 0 ? [homePitcherLHBRow, ...awayLHBatters] : [];

        // Home team sections
        const homeRHBSection = homeRHBatters.length > 0 ? [awayPitcherRHBRow, ...homeRHBatters] : [];
        const homeLHBSection = homeLHBatters.length > 0 ? [awayPitcherLHBRow, ...homeLHBatters] : [];

        // For backward compatibility, still set awayBatters and homeBatters
        awayBatters = [...awayRHBSection, ...awayLHBSection];
        homeBatters = [...homeRHBSection, ...homeLHBSection];

        // Return separate sections for splits mode
        return {
          columns: pitchColumns,
          awayBatters,
          homeBatters,
          // Add splits-specific data
          awayRHBSection,
          awayLHBSection,
          homeRHBSection,
          homeLHBSection,
          isSplitsMode: true
        };
      } else {
        // Regular mode - no splitting by handedness
        // Create pitcher performance rows for regular mode
        const createPitcherRowRegular = (pitcherData, selectedPitchType) => {
          const pitchData = pitcherData?.top4Pitches?.[pitchTypeKey]?.find(pitch =>
            pitch.pitchType === selectedPitchType
          );

          return {
            batterId: pitcherData?.pitcherId || `pitcher_overall`,
            batterName: `${pitcherData?.pitcherName || 'Pitcher'}`,
            batterTeam: pitcherData?.pitcherTeam,
            teamLogo: pitcherData?.teamLogo,
            isPitcher: true,
            isLabel: true,
            avg: pitchData?.battingAvg || 0,
            slg: pitchData?.sluggingPct || 0,
            hr: pitchData?.homeRuns || 0,
            hhPercent: pitchData?.hardHitRate || 0,
            barrelPercent: pitchData?.barrelRate || 0,
            avgXV: pitchData?.avgExitVelo || 0,
            pitchData
          };
        };

        const awayBattersOnly = battersData
          .filter(batter =>
            batter.batterTeam === awayTeam ||
            batter.batterTeam === safeGame.awayTeamFull ||
            batter.batterTeam?.toLowerCase().includes(awayTeam?.toLowerCase())
          )
          .map(batter => transformBatterForPitch(batter, awaySelectedPitchType, homePitcher));

        const homeBattersOnly = battersData
          .filter(batter =>
            batter.batterTeam === homeTeam ||
            batter.batterTeam === safeGame.homeTeamFull ||
            batter.batterTeam?.toLowerCase().includes(homeTeam?.toLowerCase())
          )
          .map(batter => transformBatterForPitch(batter, homeSelectedPitchType, awayPitcher));

        // Add pitcher performance as first row for each team
        // Away batters face home pitcher
        const homePitcherRow = homePitcher ? createPitcherRowRegular(homePitcher, awaySelectedPitchType) : null;
        awayBatters = homePitcherRow ? [homePitcherRow, ...awayBattersOnly] : awayBattersOnly;

        // Home batters face away pitcher
        const awayPitcherRow = awayPitcher ? createPitcherRowRegular(awayPitcher, homeSelectedPitchType) : null;
        homeBatters = awayPitcherRow ? [awayPitcherRow, ...homeBattersOnly] : homeBattersOnly;
      }

      console.log('Pitches data processed:', {
        awaySelectedPitchType,
        homeSelectedPitchType,
        awayBatters: awayBatters.length,
        homeBatters: homeBatters.length
      });


      return {
        columns: pitchColumns,
        awayBatters,
        homeBatters
      };
    }

    // Fallback for any unhandled analytics type
    console.log('Fallback processing for analytics type:', analyticsType);
    const columns = [
      { key: 'avg', label: 'AVG', name: 'Average' },
      { key: 'obp', label: 'OBP', name: 'On Base Percentage' },
      { key: 'slg', label: 'SLG', name: 'Slugging Percentage' }
    ];

    const awayBatters = localData.away || [];
    const homeBatters = localData.home || [];
    console.log('Fallback data:', { awayBatters: awayBatters.length, homeBatters: homeBatters.length });

    return { columns, awayBatters, homeBatters };
  };

  const { columns, awayBatters, homeBatters, awayRHBSection, awayLHBSection, homeRHBSection, homeLHBSection, isSplitsMode } = processAnalyticsData();

  // Debug final processed data
  console.log('Final processed data:', {
    columnsCount: columns.length,
    awayBattersCount: awayBatters.length,
    homeBattersCount: homeBatters.length,
    analyticsType
  });

  // Calculate mobile pagination
  const totalStatPages = Math.ceil(columns.length / statsPerPage);
  const visibleStats = columns.slice(mobileStatIndex * statsPerPage, (mobileStatIndex + 1) * statsPerPage);


  // Debug pagination
  console.log('Pagination debug:', {
    totalColumns: columns.length,
    statsPerPage,
    totalStatPages,
    mobileStatIndex,
    visibleStats: visibleStats.length
  });

  // Navigation functions
  const goToPage = (pageIndex) => {
    if (!isTransitioning && pageIndex !== mobileStatIndex) {
      setIsTransitioning(true);
      setMobileStatIndex(pageIndex);
      setTimeout(() => setIsTransitioning(false), 300);
    }
  };

  // General swipe handlers for non-pitches modes
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (totalStatPages > 1) {
        goToPage((mobileStatIndex + 1) % totalStatPages);
      }
    },
    onSwipedRight: () => {
      if (totalStatPages > 1) {
        goToPage((mobileStatIndex - 1 + totalStatPages) % totalStatPages);
      }
    },
    trackMouse: true
  });

  // Team-specific pitch pagination for pitches mode
  const getTeamPitchData = (team, handednessOverride = null) => {
    if (analyticsType !== 'pitches' || !localData) {
      return { totalPitchPages: 0, availablePitches: [], availablePitchesRHB: [], availablePitchesLHB: [] };
    }

    // Get pitcher data
    let pitchersData = [];
    if (localData.rawPitchersData && Array.isArray(localData.rawPitchersData)) {
      pitchersData = localData.rawPitchersData;
    } else if (localData.pitchers && Array.isArray(localData.pitchers)) {
      pitchersData = localData.pitchers;
    } else if (typeof localData === 'object' && !Array.isArray(localData)) {
      const cachedData = localData[selectedYear] || [];
      pitchersData = cachedData;
    }

    if (pitchersData && pitchersData.length > 0) {
      // Check if this is cached data without proper pitcher structure
      if (pitchersData[0].batterName) {
        return { totalPitchPages: 0, availablePitches: [] };
      }

      // Find the opposing pitcher for this team
      const opposingTeam = team === safeGame.away ? safeGame.home : safeGame.away;
      const opposingPitcher = pitchersData.find(p => p.pitcherTeam === opposingTeam);

      if (!opposingPitcher) {
        return { totalPitchPages: 0, availablePitches: [] };
      }

      // For splits mode, determine the correct handedness based on the team
      let pitchTypeKey;
      if (pitchesDisplayMode === 'all') {
        pitchTypeKey = 'vsAll';
      } else if (pitchesDisplayMode === 'splits') {
        // In splits mode: away team faces RHB pitches, home team faces LHB pitches
        pitchTypeKey = team === safeGame.away ? 'vsRHB' : 'vsLHB';
      } else {
        pitchTypeKey = pitcherHandFilter === 'vsRHP' ? 'vsRHP' : 'vsLHB';
      }

      // Get pitch types from the opposing pitcher
      const pitchTypes = opposingPitcher?.top4Pitches?.[pitchTypeKey] || [];
      const availablePitches = pitchTypes.map(pitch => pitch.pitchType);

      // Also get RHB and LHB specific pitches for splits mode
      const rhbPitchTypes = opposingPitcher?.top4Pitches?.vsRHB || [];
      const lhbPitchTypes = opposingPitcher?.top4Pitches?.vsLHB || [];
      const availablePitchesRHB = rhbPitchTypes.map(pitch => pitch.pitchType);
      const availablePitchesLHB = lhbPitchTypes.map(pitch => pitch.pitchType);

      return {
        totalPitchPages: availablePitches.length,
        availablePitches,
        availablePitchesRHB,
        availablePitchesLHB,
        opposingPitcher
      };
    }
    return { totalPitchPages: 0, availablePitches: [], availablePitchesRHB: [], availablePitchesLHB: [] };
  };

  // Get team-specific pitch data
  const awayPitchData = getTeamPitchData(safeGame.away);
  const homePitchData = getTeamPitchData(safeGame.home);

  // Debug pitch pagination
  console.log('Team-specific pitch pagination debug:', {
    analyticsType,
    away: {
      totalPitchPages: awayPitchData.totalPitchPages,
      availablePitches: awayPitchData.availablePitches,
      currentIndex: awayPitchIndex,
      opposingPitcher: awayPitchData.opposingPitcher?.pitcherName
    },
    home: {
      totalPitchPages: homePitchData.totalPitchPages,
      availablePitches: homePitchData.availablePitches,
      currentIndex: homePitchIndex,
      opposingPitcher: homePitchData.opposingPitcher?.pitcherName
    }
  });

  // Team-specific pitch navigation functions
  const goToAwayPitch = (pitchIndex) => {
    if (pitchIndex !== awayPitchIndex && pitchIndex >= 0 && pitchIndex < awayPitchData.totalPitchPages) {
      setAwayPitchIndex(pitchIndex);
    }
  };

  const goToHomePitch = (pitchIndex) => {
    if (pitchIndex !== homePitchIndex && pitchIndex >= 0 && pitchIndex < homePitchData.totalPitchPages) {
      setHomePitchIndex(pitchIndex);
    }
  };

  // Team-specific swipe handlers for pitches mode
  const awayPitchSwipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (awayPitchData.totalPitchPages > 1) {
        goToAwayPitch((awayPitchIndex + 1) % awayPitchData.totalPitchPages);
      }
    },
    onSwipedRight: () => {
      if (awayPitchData.totalPitchPages > 1) {
        goToAwayPitch((awayPitchIndex - 1 + awayPitchData.totalPitchPages) % awayPitchData.totalPitchPages);
      }
    },
    trackMouse: true
  });

  const homePitchSwipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (homePitchData.totalPitchPages > 1) {
        goToHomePitch((homePitchIndex + 1) % homePitchData.totalPitchPages);
      }
    },
    onSwipedRight: () => {
      if (homePitchData.totalPitchPages > 1) {
        goToHomePitch((homePitchIndex - 1 + homePitchData.totalPitchPages) % homePitchData.totalPitchPages);
      }
    },
    trackMouse: true
  });

  // Separate swipe handlers for splits mode RHB/LHB sections
  const awayRHBSwipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (awayPitchData.availablePitchesRHB?.length > 1) {
        setAwayRHBPitchIndex((awayRHBPitchIndex + 1) % awayPitchData.availablePitchesRHB.length);
      }
    },
    onSwipedRight: () => {
      if (awayPitchData.availablePitchesRHB?.length > 1) {
        setAwayRHBPitchIndex((awayRHBPitchIndex - 1 + awayPitchData.availablePitchesRHB.length) % awayPitchData.availablePitchesRHB.length);
      }
    },
    trackMouse: true
  });

  const awayLHBSwipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (awayPitchData.availablePitchesLHB?.length > 1) {
        setAwayLHBPitchIndex((awayLHBPitchIndex + 1) % awayPitchData.availablePitchesLHB.length);
      }
    },
    onSwipedRight: () => {
      if (awayPitchData.availablePitchesLHB?.length > 1) {
        setAwayLHBPitchIndex((awayLHBPitchIndex - 1 + awayPitchData.availablePitchesLHB.length) % awayPitchData.availablePitchesLHB.length);
      }
    },
    trackMouse: true
  });

  const homeRHBSwipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (homePitchData.availablePitchesRHB?.length > 1) {
        setHomeRHBPitchIndex((homeRHBPitchIndex + 1) % homePitchData.availablePitchesRHB.length);
      }
    },
    onSwipedRight: () => {
      if (homePitchData.availablePitchesRHB?.length > 1) {
        setHomeRHBPitchIndex((homeRHBPitchIndex - 1 + homePitchData.availablePitchesRHB.length) % homePitchData.availablePitchesRHB.length);
      }
    },
    trackMouse: true
  });

  const homeLHBSwipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (homePitchData.availablePitchesLHB?.length > 1) {
        setHomeLHBPitchIndex((homeLHBPitchIndex + 1) % homePitchData.availablePitchesLHB.length);
      }
    },
    onSwipedRight: () => {
      if (homePitchData.availablePitchesLHB?.length > 1) {
        setHomeLHBPitchIndex((homeLHBPitchIndex - 1 + homePitchData.availablePitchesLHB.length) % homePitchData.availablePitchesLHB.length);
      }
    },
    trackMouse: true
  });

  // Helper function to render a handedness-specific table section
  const renderHandednessSection = (sectionData, sectionTitle, pitchType, pitchUsage, swipeHandlers, pitchIndex, availablePitches, setPitchIndex, opposingPitcher = null) => {

    const visibleStats = columns.filter(col => col.pitchName);

    return (
      <div className="mb-3">
        {/* Pitcher info header */}
        {opposingPitcher && (
          <div style={{
            textAlign: 'center',
            fontSize: '0.8rem',
            fontWeight: 'bold',
            backgroundColor: '#212529',
            color: '#ffc107',
            padding: '6px',
            marginBottom: '4px',
            borderRadius: '4px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <img
                src={getTeamLogoUrl(opposingPitcher.pitcherTeam)}
                alt={opposingPitcher.pitcherTeam}
                style={{ width: '20px', height: '20px' }}
              />
              <span>{opposingPitcher.pitcherName}</span>
              {opposingPitcher.pitcherThrows && (
                <span>({opposingPitcher.pitcherThrows})</span>
              )}
            </div>
          </div>
        )}

        {/* Pitch type header */}
        <div style={{
          textAlign: 'center',
          fontSize: '0.7rem',
          fontWeight: 'bold',
          backgroundColor: '#f8f9fa',
          padding: '4px',
          marginBottom: '4px',
          borderRadius: '4px'
        }}>
          {pitchType ? `${pitchType} (${pitchUsage}%)` : 'PITCH'}
        </div>

        {/* Table with swipe handlers */}
        <div {...swipeHandlers} className={`table-container ${analyticsType === 'matchups' ? 'mobile-matchups-table' : ''}`}>
          <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th className="text-start" style={{ width: `${mobileNameWidth}%`, fontSize: '0.7rem', padding: '4px' }}>
                  {/* Pagination dots */}
                  {availablePitches && availablePitches.length > 1 ? (
                    <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                      {Array.from({ length: availablePitches.length }, (_, index) => (
                        <button
                          key={index}
                          onClick={() => setPitchIndex(index)}
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            border: 'none',
                            background: index === pitchIndex ? '#28a745' : '#dee2e6',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            padding: '0',
                            margin: '0'
                          }}
                          aria-label={`Go to pitch ${index + 1}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: '0.65rem', color: '#6c757d' }}>Player</span>
                  )}
                </th>
                {visibleStats.map(stat => (
                  <th key={stat.key} style={{ width: `${mobileStatWidth}%`, textAlign: 'center', fontSize: '0.7rem' }} title={stat.name}>
                    {stat.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sectionData.map((row, index) => {
                // Handle pitcher stats rows
                if (row.isLabel && row.isPitcher) {
                  return (
                    <tr key={index} style={{ backgroundColor: '#495057' }}>
                      <td
                        style={{
                          width: `${mobileNameWidth}% !important`,
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                          color: '#ffffff',
                          padding: '6px',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const playerId = row.batterId || row.pitcherId || row.batterID || row.playerID || row.playerId || row.id;
                          if (onPlayerClick) {
                            onPlayerClick(
                              safeGame.gameIDmlb,
                              playerId || `mlb_pitcher_${row.batterName}`,
                              row.batterName,
                              null,
                              safeGame.away,
                              safeGame.home
                            );
                          }
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <img
                            src={row.teamLogo || getTeamLogoUrl(row.batterTeam)}
                            alt={row.batterTeam}
                            style={{ width: '20px', height: '20px', marginRight: '8px' }}
                          />
                          {row.batterName}
                        </div>
                      </td>
                      {visibleStats.map(stat => (
                        <td
                          key={stat.key}
                          style={{
                            width: `${mobileStatWidth}% !important`,
                            padding: '6px',
                            fontWeight: 'bold',
                            ...getStatCellStyle(stat.key, row[stat.key]),
                            ...((!['avg', 'slg', 'ops'].includes(stat.key) &&
                                 !stat.key.includes('battingAvg') &&
                                 !stat.key.includes('slg') &&
                                 !stat.key.includes('ops')) ?
                                { backgroundColor: 'transparent', color: '#ffffff' } : {})
                          }}
                          title={row.atBats !== undefined ? `${row.atBats} AB` : undefined}
                        >
                          {stat.key === 'ab' ? '-' : formatValue(stat.key, row[stat.key], row)}
                        </td>
                      ))}
                    </tr>
                  );
                }

                // Handle regular batter rows
                return (
                  <tr key={index}>
                    <td
                      className="player-name-cell"
                      style={{
                        backgroundImage: `url(${getTeamLogoUrl(row.batterTeam)})`,
                        width: `${mobileNameWidth}% !important`,
                        fontSize: '0.8rem',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const playerId = row.batterId || row.batterID || row.playerID || row.playerId || row.id;
                        if (onPlayerClick && playerId) {
                          onPlayerClick(
                            safeGame.gameIDmlb,
                            playerId,
                            row.batterName,
                            null,
                            safeGame.away,
                            safeGame.home
                          );
                        }
                      }}
                    >
                      <span>
                        {formatPlayerName ? formatPlayerName(row.batterName, true, false, 'mlb') : row.batterName}
                        {row.batterBats && (
                          <span style={{ fontSize: '0.65rem', color: '#6c757d', marginLeft: '4px' }}>
                            ({row.batterBats.charAt(0)})
                          </span>
                        )}
                      </span>
                    </td>
                    {visibleStats.map(stat => (
                      <td
                        key={stat.key}
                        style={{
                          ...getStatCellStyle(stat.key, row[stat.key]),
                          width: `${mobileStatWidth}% !important`
                        }}
                        title={row.atBats !== undefined ? `${row.atBats} AB` : undefined}
                      >
                        {formatValue(stat.key, row[stat.key], row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  // Format time display
  const formatTime = (timeStr, gameStatus) => {
    if (!timeStr) return 'TBD';
    if (gameStatus === 'Live') return 'LIVE';
    if (isGameFinished(gameStatus)) return gameStatus.toUpperCase();

    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }

    const [hour, minute] = timeStr.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const timeDisplay = formatTime(gameTime, gameStatus);
  const isLive = gameStatus === 'Live';

  // Calculate mobile column widths - ensure name column is wide enough for all modes
  // For 3 columns: name gets ~55%, each stat gets ~15%
  const mobileStatWidth = 18; // Fixed width for consistency
  const mobileNameWidth = Math.max(46, 100 - (mobileStatWidth * visibleStats.length)); // Ensure minimum 55%

  // Debug log for column widths
  console.log('Column width calculation:', {
    analyticsType,
    visibleStatsLength: visibleStats.length,
    mobileStatWidth,
    mobileNameWidth,
    visibleStats: visibleStats.map(s => s.label)
  });

  // Get matchup info helper
  const getMatchupInfo = (row) => {
    if (analyticsType === 'matchups') {
      return row.matchupInfo || '';
    }
    return '';
  };

  // Format values for display
  const formatValue = (key, value, row = null) => {
    if (value === undefined || value === null) return '-';

    // If at-bats is 0, show dashes for rate stats like desktop
    if (row && row.atBats === 0 && (key === 'avg' || key === 'obp' || key === 'slg' || key === 'ops')) {
      return '-';
    }

    // Percentage stats should not have leading 0 and should be 3 decimal places
    if (key === 'avg' || key === 'obp' || key === 'slg') {
      return value.toFixed(3).replace(/^0\./, '.');
    }

    // Other stats (HR, hits, etc.)
    return value;
  };

  // Stat cell styling with tiered coloring
  const getStatCellStyle = (statKey, value) => {
    const baseStyle = {
      textAlign: 'center',
      fontSize: '0.7rem'
    };

    // Apply tier coloring for AVG, SLG, and OPS (handle both matchups, performance, and pitches naming)
    const statType = (statKey.includes('battingAvg') || statKey.endsWith('_avg') || statKey === 'avg') ? 'battingAvg' :
                     (statKey.includes('slg') || statKey.endsWith('_slg') || statKey === 'slg') ? 'slg' :
                     (statKey.includes('ops') || statKey.endsWith('_ops') || statKey === 'ops') ? 'ops' : null;

    if (value === undefined || value === null) return baseStyle;

    if (statType === 'battingAvg') {
      if (value >= 0.350) {
        return { ...baseStyle, backgroundColor: 'green', color: 'yellow', fontWeight: 'bold' };
      } else if (value >= 0.320) {
        return { ...baseStyle, backgroundColor: '#F59E0B', color: '#1E40AF', fontWeight: 'bold' };
      } else if (value >= 0.280) {
        return { ...baseStyle, backgroundColor: '#3B82F6', color: 'white', fontWeight: 'bold' };
      }
    } else if (statType === 'slg') {
      if (value >= 0.600) {
        return { ...baseStyle, backgroundColor: 'green', color: 'yellow', fontWeight: 'bold' };
      } else if (value >= 0.500) {
        return { ...baseStyle, backgroundColor: '#F59E0B', color: '#1E40AF', fontWeight: 'bold' };
      } else if (value >= 0.450) {
        return { ...baseStyle, backgroundColor: '#3B82F6', color: 'white', fontWeight: 'bold' };
      }
    } else if (statType === 'ops') {
      if (value >= 1.000) {
        return { ...baseStyle, backgroundColor: 'green', color: 'yellow', fontWeight: 'bold' };
      } else if (value >= 0.900) {
        return { ...baseStyle, backgroundColor: '#F59E0B', color: '#1E40AF', fontWeight: 'bold' };
      } else if (value >= 0.800) {
        return { ...baseStyle, backgroundColor: '#3B82F6', color: 'white', fontWeight: 'bold' };
      }
    }

    return baseStyle;
  };

  return (
    <div className={`card game-card ${analyticsType === 'matchups' ? 'mobile-matchups-game' : ''}`}>
      <div className="card-body" style={{ padding: '5px' }}>
        {/* Game Header */}
        <div
          className="game-header-container"
          style={{
            position: 'relative',
            cursor: 'pointer',
            userSelect: 'none'
          }}
          onClick={() => onCollapseToggle && onCollapseToggle(!isCollapsed)}
        >
          {/* Game time */}
          <div style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            fontSize: isCollapsed ? '0.7rem' : '0.9rem',
            color: isLive ? '#dc3545' : '#6c757d',
            fontWeight: '500'
          }}>
            {timeDisplay}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            {/* Team logos and scores */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {(gameStatus !== 'Scheduled' && safeGame.awayScore !== null && safeGame.awayScore !== undefined) && (
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold', marginRight: '8px' }}>
                  {safeGame.awayScore}
                </span>
              )}
              <img
                src={safeGame.awayLogo || getTeamLogoUrl(safeGame.away)}
                alt={safeGame.away}
                style={{ width: '32px', height: '32px', marginRight: '8px' }}
              />
              @
              <img
                src={safeGame.homeLogo || getTeamLogoUrl(safeGame.home)}
                alt={safeGame.home}
                style={{ width: '32px', height: '32px', margin: '0 8px' }}
              />
              {(gameStatus !== 'Scheduled' && safeGame.homeScore !== null && safeGame.homeScore !== undefined) && (
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold', marginLeft: '8px' }}>
                  {safeGame.homeScore}
                </span>
              )}
              {/* Collapse indicator */}
              <span style={{
                marginLeft: '12px',
                fontSize: '1.2rem',
                color: '#6c757d'
              }}>
                {isCollapsed ? '▼' : '▲'}
              </span>
            </div>

            {/* Team names */}
            <div style={{
              fontSize: '0.9rem',
              fontWeight: 'bold',
              color: '#6c757d',
              textAlign: 'center'
            }}>
              {safeGame.awayTeamFull || safeGame.away} vs {safeGame.homeTeamFull || safeGame.home}
            </div>
          </div>
        </div>

        {!isCollapsed && (
          <>
            {/* Analytics type toggle buttons */}
            <div className="d-flex justify-content-center mt-2 mb-2">
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${analyticsType === 'matchups' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setAnalyticsType('matchups')}
                  style={{ fontSize: '10px' }}
                >
                  BvP
                </button>
                <button
                  type="button"
                  className={`btn ${analyticsType === 'performance' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setAnalyticsType('performance')}
                  style={{ fontSize: '10px' }}
                >
                  Last X
                </button>
                <button
                  type="button"
                  className={`btn ${analyticsType === 'pitches' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setAnalyticsType('pitches')}
                  style={{ fontSize: '10px' }}
                >
                  Pitches
                </button>
              </div>
            </div>

            {/* BvP year selector */}
            {analyticsType === 'matchups' && (
              <div className="d-flex justify-content-center mt-1 mb-2">
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn ${selectedYear === 'alltime' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setSelectedYear('alltime')}
                    style={{ fontSize: '9px' }}
                  >
                    Combined
                  </button>
                  <button
                    type="button"
                    className={`btn ${selectedYear === '2025' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setSelectedYear('2025')}
                    style={{ fontSize: '9px' }}
                  >
                    2025
                  </button>
                  <button
                    type="button"
                    className={`btn ${selectedYear === '2024' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setSelectedYear('2024')}
                    style={{ fontSize: '9px' }}
                  >
                    2024
                  </button>
                </div>
              </div>
            )}

            {/* Performance view toggles */}
            {analyticsType === 'performance' && (
              <div className="d-flex justify-content-center mt-1 mb-2" style={{ gap: '10px' }}>
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn ${performanceView === 'last7' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPerformanceView('last7')}
                    style={{ fontSize: '9px' }}
                  >
                    Last 7
                  </button>
                  <button
                    type="button"
                    className={`btn ${performanceView === 'last14' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPerformanceView('last14')}
                    style={{ fontSize: '9px' }}
                  >
                    Last 14
                  </button>
                  <button
                    type="button"
                    className={`btn ${performanceView === 'last30' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPerformanceView('last30')}
                    style={{ fontSize: '9px' }}
                  >
                    Last 30
                  </button>
                </div>

                {/* Pitcher handedness filter toggles for Performance mode */}
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn ${pitcherHandFilter === 'vsAll' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPitcherHandFilter('vsAll')}
                    style={{ fontSize: '8px' }}
                  >
                    Both
                  </button>
                  <button
                    type="button"
                    className={`btn ${pitcherHandFilter === 'vsRHP' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPitcherHandFilter('vsRHP')}
                    style={{ fontSize: '8px' }}
                  >
                    vs RHP
                  </button>
                  <button
                    type="button"
                    className={`btn ${pitcherHandFilter === 'vsLHP' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPitcherHandFilter('vsLHP')}
                    style={{ fontSize: '8px' }}
                  >
                    vs LHP
                  </button>
                </div>
              </div>
            )}

            {/* Pitches view toggles */}
            {analyticsType === 'pitches' && (
              <div className="d-flex justify-content-center mt-1 mb-2" style={{ gap: '10px' }}>
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn ${pitchesDisplayMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPitchesDisplayMode('all')}
                    style={{ fontSize: '9px' }}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`btn ${pitchesDisplayMode === 'splits' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPitchesDisplayMode('splits')}
                    style={{ fontSize: '9px' }}
                  >
                    Splits
                  </button>
                </div>
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn ${pitchesView === 'main' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPitchesView('main')}
                    style={{ fontSize: '9px' }}
                  >
                    Main
                  </button>
                  <button
                    type="button"
                    className={`btn ${pitchesView === 'advanced' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPitchesView('advanced')}
                    style={{ fontSize: '9px' }}
                  >
                    Advanced
                  </button>
                </div>
              </div>
            )}

            {/* Analytics Content */}
            {localLoading ? (
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : !localData ? (
              <div className="text-center text-muted">
                <small>No analytics data available</small>
              </div>
            ) : (
              <div {...swipeHandlers} className="d-flex flex-column" style={{ gap: '15px' }}>
                {/* Mobile swipe dots indicator */}
                {totalStatPages > 1 && (
                  <div className="d-flex justify-content-center align-items-center mb-2" style={{ gap: '6px' }}>
                    {Array.from({ length: totalStatPages }, (_, index) => (
                      <button
                        key={index}
                        onClick={() => goToPage(index)}
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          border: 'none',
                          background: index === mobileStatIndex ? '#007bff' : '#dee2e6',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          padding: '0',
                          margin: '0'
                        }}
                        aria-label={`Go to page ${index + 1}`}
                      />
                    ))}
                  </div>
                )}


                {/* BvP Pitcher Info for Away Team */}
                {analyticsType === 'matchups' && (
                  <div style={{
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    backgroundColor: '#212529',
                    color: '#ffc107',
                    padding: '6px',
                    marginBottom: '8px',
                    borderRadius: '4px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <img
                        src={getTeamLogoUrl(safeGame.home)}
                        alt={safeGame.home}
                        style={{ width: '20px', height: '20px' }}
                      />
                      {safeGame.homeProbablePitcher ? (
                        <span
                          style={{ cursor: 'pointer', textDecoration: 'none' }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const pitcherId = safeGame.homeProbablePitcher?.playerID || safeGame.homeProbablePitcher?.pitcherId || safeGame.homeProbablePitcher?.id;
                            if (onPlayerClick && pitcherId) {
                              onPlayerClick(
                                safeGame.gameIDmlb,
                                pitcherId,
                                safeGame.homeProbablePitcher.fullName,
                                null,
                                safeGame.away,
                                safeGame.home
                              );
                            }
                          }}
                        >
                          vs. {isPotentialOpener(safeGame, true) && <sup>1</sup>}{safeGame.homeProbablePitcher.fullName || 'TBD'}{isPotentialOpener(safeGame, true) ? '*' : ''}{safeGame.homeProbablePitcher.throws ? ` (${safeGame.homeProbablePitcher.throws})` : ''}
                          {(() => {
                            const prev = getPreviousProbablePitcher(safeGame, true);
                            return prev ? (
                              <div style={{ opacity: 0.85 }}><sup>2</sup>{prev.fullName}{prev.throws ? ` (${prev.throws})` : ''}</div>
                            ) : null;
                          })()}
                        </span>
                      ) : (
                        'vs. TBD'
                      )}
                    </div>
                  </div>
                )}

                {/* Away Team Table(s) */}
                {analyticsType === 'pitches' && isSplitsMode ? (
                  // Splits mode: separate tables for RHB and LHB
                  <>
                    {/* Away RHB Section */}
                    {renderHandednessSection(
                      awayRHBSection,
                      "Away RHB",
                      awayPitchData.availablePitchesRHB?.[awayRHBPitchIndex],
                      (() => {
                        const homePitcher = awayPitchData.opposingPitcher;
                        const pitchType = awayPitchData.availablePitchesRHB?.[awayRHBPitchIndex];
                        const pitchData = homePitcher?.top4Pitches?.vsRHB?.find(p => p.pitchType === pitchType);
                        return pitchData?.usage || '0';
                      })(),
                      awayRHBSwipeHandlers,
                      awayRHBPitchIndex,
                      awayPitchData.availablePitchesRHB,
                      setAwayRHBPitchIndex,
                      awayPitchData.opposingPitcher
                    )}

                    {/* Away LHB Section */}
                    {renderHandednessSection(
                      awayLHBSection,
                      "Away LHB",
                      awayPitchData.availablePitchesLHB?.[awayLHBPitchIndex],
                      (() => {
                        const homePitcher = awayPitchData.opposingPitcher;
                        const pitchType = awayPitchData.availablePitchesLHB?.[awayLHBPitchIndex];
                        const pitchData = homePitcher?.top4Pitches?.vsLHB?.find(p => p.pitchType === pitchType);
                        return pitchData?.usage || '0';
                      })(),
                      awayLHBSwipeHandlers,
                      awayLHBPitchIndex,
                      awayPitchData.availablePitchesLHB,
                      setAwayLHBPitchIndex,
                      awayPitchData.opposingPitcher
                    )}
                  </>
                ) : (
                  // Regular single table mode
                  <div
                    {...(analyticsType === 'pitches' ? awayPitchSwipeHandlers : {})}
                    className={`table-container ${analyticsType === 'matchups' ? 'mobile-matchups-table' : ''}`}
                  >
                  <Table bordered size="sm" className="text-center" style={{
                    tableLayout: 'fixed',
                    ...(awayBatters.length === 0 ? { height: 'auto', minHeight: 'unset' } : {})
                  }}>
                    <thead>


                      {/* For pitches mode, add pitch name header row like desktop */}
                      {analyticsType === 'pitches' && (
                        <tr>
                          <th className="text-start" style={{ width: `${mobileNameWidth}%`, fontSize: '0.7rem', padding: '4px' }}>
                            {/* Away team pitch pager in header */}
                            {awayPitchData.totalPitchPages > 1 ? (
                              <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                                <span style={{ display: 'none', fontSize: '0.65rem', color: '#6c757d' }}>
                                  {awayPitchData.opposingPitcher?.pitcherName?.split(' ').pop() || 'Pitcher'}:
                                </span>
                                {Array.from({ length: awayPitchData.totalPitchPages }, (_, index) => (
                                  <button
                                    key={index}
                                    onClick={() => goToAwayPitch(index)}
                                    style={{
                                      width: '10px',
                                      height: '10px',
                                      borderRadius: '50%',
                                      border: 'none',
                                      background: index === awayPitchIndex ? '#28a745' : '#dee2e6',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      padding: '0',
                                      margin: '0'
                                    }}
                                    aria-label={`Go to ${safeGame.away} pitch ${index + 1}`}
                                  />
                                ))}
                              </div>
                            ) : (
                              <span style={{ fontSize: '0.65rem', color: '#6c757d' }}>Player</span>
                            )}
                          </th>
                          {(() => {
                            // Group visible stats by pitch name
                            const pitchGroups = {};
                            visibleStats.forEach(stat => {
                              if (stat.pitchName) {
                                if (!pitchGroups[stat.pitchName]) {
                                  pitchGroups[stat.pitchName] = [];
                                }
                                pitchGroups[stat.pitchName].push(stat);
                              }
                            });

                            return Object.entries(pitchGroups).map(([pitchName, stats]) => {
                              // For away team table, show home pitcher's percentage (since away batters face home pitcher)
                              let displayPitchName = pitchName;
                              if (stats[0]?.pitchData) {
                                const { homePitcher, awaySelectedPitchType, awayRHBSelectedPitchType, awayLHBSelectedPitchType,
                                  pitchTypeKey, pitchesDisplayMode } = stats[0].pitchData;
                                if (homePitcher) {
                                  // In splits mode, determine which pitch type to use based on handedness
                                  let selectedPitch = awaySelectedPitchType;
                                  let actualPitchTypeKey = pitchTypeKey;

                                  if (pitchesDisplayMode === 'splits') {
                                    // Check if we have RHB or LHB batters in this section
                                    const hasRHBBatters = awayBatters.some(b => b.batterBats === 'Right' && !b.isPitcher);
                                    const hasLHBBatters = awayBatters.some(b => b.batterBats === 'Left' && !b.isPitcher);

                                    // If we have RHB batters first, use RHB pitch, otherwise use LHB pitch
                                    if (hasRHBBatters && !hasLHBBatters) {
                                      selectedPitch = awayRHBSelectedPitchType;
                                      actualPitchTypeKey = 'vsRHB';
                                    } else if (!hasRHBBatters && hasLHBBatters) {
                                      selectedPitch = awayLHBSelectedPitchType;
                                      actualPitchTypeKey = 'vsLHB';
                                    } else {
                                      // Mixed or default to RHB for the first group
                                      const firstBatterIndex = awayBatters.findIndex(b => !b.isPitcher);
                                      if (firstBatterIndex >= 0 && awayBatters[firstBatterIndex].batterBats === 'Left') {
                                        selectedPitch = awayLHBSelectedPitchType;
                                        actualPitchTypeKey = 'vsLHB';
                                      } else {
                                        selectedPitch = awayRHBSelectedPitchType;
                                        actualPitchTypeKey = 'vsRHB';
                                      }
                                    }
                                  }

                                  const pitcherPitchData = homePitcher.top4Pitches?.[actualPitchTypeKey]?.find(p => p.pitchType === selectedPitch);
                                  displayPitchName = formatPitchName(selectedPitch, pitcherPitchData);
                                  console.log('Away team header - Home pitcher:', homePitcher.pitcherName, 'Pitch:', selectedPitch, 'actualPitchTypeKey:', actualPitchTypeKey, 'Available pitches:', homePitcher.top4Pitches?.[actualPitchTypeKey]?.map(p => p.pitchType), 'Found data:', !!pitcherPitchData, 'Usage:', pitcherPitchData?.usage);
                                }
                              }

                              return (
                                <th
                                  key={pitchName}
                                  colSpan={stats.length}
                                  style={{
                                    width: `${mobileStatWidth * stats.length}%`,
                                    textAlign: 'center',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    backgroundColor: '#f8f9fa'
                                  }}
                                >
                                  {displayPitchName}
                                </th>
                              );
                            });
                          })()}
                        </tr>
                      )}

                      <tr>
                        <th
                          className="text-start player-name-cell"
                          style={{
                            width: `${mobileNameWidth}% !important`,
                            fontSize: '0.75rem',
                            color: 'yellow',
                            textTransform: 'none'
                          }}
                        >
                          {analyticsType === 'matchups' ? 'Player' :
                            analyticsType === 'performance' ? (
                              performanceView === 'last7' ? 'Last 7' :
                              performanceView === 'last14' ? 'Last 14' : 'Last 30'
                            ) : (analyticsType === 'pitches' && pitchesDisplayMode === 'splits') ? (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '0.65rem' }}>
                                <img
                                  src={getTeamLogoUrl(safeGame.home)}
                                  alt={safeGame.home}
                                  style={{ width: '14px', height: '14px' }}
                                />
                                {safeGame.homeProbablePitcher ? (() => {
                                  const opener = isPotentialOpener(safeGame, true);
                                  const prev = opener ? getPreviousProbablePitcher(safeGame, true) : null;
                                  return (
                                    <>
                                      {opener && <sup>1</sup>}{safeGame.homeProbablePitcher.fullName || 'TBD'}{opener ? '*' : ''}{safeGame.homeProbablePitcher.throws ? ` (${safeGame.homeProbablePitcher.throws})` : ''}
                                      {prev && (
                                        <div style={{ opacity: 0.85 }}><sup>2</sup>{prev.fullName}{prev.throws ? ` (${prev.throws})` : ''}</div>
                                      )}
                                    </>
                                  );
                                })() : 'TBD'}
                              </div>
                            ) : 'Pitch'}
                        </th>
                        {visibleStats.map(stat => (
                          <th key={stat.key} style={{ width: `${mobileStatWidth}%`, textAlign: 'center', fontSize: '0.7rem' }} title={stat.name}>
                            {stat.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {console.log('Rendering away batters:', awayBatters.length, awayBatters.slice(0, 2))}
                      {awayBatters.length === 0 && (
                        <tr>
                          <td colSpan={visibleStats.length + 1} className="text-center text-muted">
                            No away batters data
                          </td>
                        </tr>
                      )}
                      {awayBatters.map((row, index) => (
                        <tr key={index}>
                          <td
                            className="player-name-cell"
                            style={{
                              backgroundImage: `url(${getTeamLogoUrl(row.batterTeam || safeGame.away)})`,
                              width: `${mobileNameWidth}% !important`,
                              fontSize: '0.8rem',
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const playerId = row.batterId || row.batterID || row.playerID || row.playerId || row.id;

                              if (onPlayerClick && playerId) {
                                onPlayerClick(
                                  safeGame.gameIDmlb,
                                  playerId,
                                  row.batterName,
                                  null,
                                  safeGame.away,
                                  safeGame.home
                                );
                              }
                            }}
                          >
                            <span>
                              {formatPlayerName ? formatPlayerName(row.batterName, true, false, 'mlb') : row.batterName}
                              {row.batterBats && (
                                <span style={{ fontSize: '0.65rem', color: '#6c757d', marginLeft: '4px' }}>
                                  ({row.batterBats.charAt(0)})
                                </span>
                              )}
                            </span>
                          </td>
                          {visibleStats.map(stat => (
                            <td
                              key={stat.key}
                              style={{
                                ...getStatCellStyle(stat.key, row[stat.key]),
                                width: `${mobileStatWidth}% !important`
                              }}
                            >
                              {formatValue(stat.key, row[stat.key], row)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                )}

                {/* BvP Pitcher Info for Home Team */}
                {analyticsType === 'matchups' && (
                  <div style={{
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    backgroundColor: '#212529',
                    color: '#ffc107',
                    padding: '6px',
                    marginBottom: '8px',
                    borderRadius: '4px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <img
                        src={getTeamLogoUrl(safeGame.away)}
                        alt={safeGame.away}
                        style={{ width: '20px', height: '20px' }}
                      />
                      {safeGame.awayProbablePitcher ? (
                        <span
                          style={{ cursor: 'pointer', textDecoration: 'none' }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const pitcherId = safeGame.awayProbablePitcher?.playerID || safeGame.awayProbablePitcher?.pitcherId || safeGame.awayProbablePitcher?.id;
                            if (onPlayerClick && pitcherId) {
                              onPlayerClick(
                                safeGame.gameIDmlb,
                                pitcherId,
                                safeGame.awayProbablePitcher.fullName,
                                null,
                                safeGame.away,
                                safeGame.home
                              );
                            }
                          }}
                        >
                          vs. {isPotentialOpener(safeGame, false) && <sup>1</sup>}{safeGame.awayProbablePitcher.fullName || 'TBD'}{isPotentialOpener(safeGame, false) ? '*' : ''}{safeGame.awayProbablePitcher.throws ? ` (${safeGame.awayProbablePitcher.throws})` : ''}
                          {(() => {
                            const prev = getPreviousProbablePitcher(safeGame, false);
                            return prev ? (
                              <div style={{ opacity: 0.85 }}><sup>2</sup>{prev.fullName}{prev.throws ? ` (${prev.throws})` : ''}</div>
                            ) : null;
                          })()}
                        </span>
                      ) : (
                        'vs. TBD'
                      )}
                    </div>
                  </div>
                )}

                {/* Home Team Tables */}
                {analyticsType === 'pitches' && isSplitsMode ? (
                  // Splits mode: separate tables for RHB and LHB
                  <>
                    {/* Home RHB Section */}
                    {renderHandednessSection(
                      homeRHBSection,
                      "Home RHB",
                      homePitchData.availablePitchesRHB?.[homeRHBPitchIndex],
                      (() => {
                        const awayPitcher = homePitchData.opposingPitcher;
                        const pitchType = homePitchData.availablePitchesRHB?.[homeRHBPitchIndex];
                        const pitchData = awayPitcher?.top4Pitches?.vsRHB?.find(p => p.pitchType === pitchType);
                        return pitchData?.usage || '0';
                      })(),
                      homeRHBSwipeHandlers,
                      homeRHBPitchIndex,
                      homePitchData.availablePitchesRHB,
                      setHomeRHBPitchIndex,
                      homePitchData.opposingPitcher
                    )}

                    {/* Home LHB Section */}
                    {renderHandednessSection(
                      homeLHBSection,
                      "Home LHB",
                      homePitchData.availablePitchesLHB?.[homeLHBPitchIndex],
                      (() => {
                        const awayPitcher = homePitchData.opposingPitcher;
                        const pitchType = homePitchData.availablePitchesLHB?.[homeLHBPitchIndex];
                        const pitchData = awayPitcher?.top4Pitches?.vsLHB?.find(p => p.pitchType === pitchType);
                        return pitchData?.usage || '0';
                      })(),
                      homeLHBSwipeHandlers,
                      homeLHBPitchIndex,
                      homePitchData.availablePitchesLHB,
                      setHomeLHBPitchIndex,
                      homePitchData.opposingPitcher
                    )}
                  </>
                ) : (
                  // Regular single table mode
                  <div
                    {...(analyticsType === 'pitches' ? homePitchSwipeHandlers : {})}
                    className={`table-container ${analyticsType === 'matchups' ? 'mobile-matchups-table' : ''}`}
                  >
                    <Table bordered size="sm" className="text-center" style={{
                      tableLayout: 'fixed',
                      ...(homeBatters.length === 0 ? { height: 'auto', minHeight: 'unset' } : {})
                    }}>
                      <thead>
                        {/* For pitches mode, add pitch name header row like desktop */}
                        {analyticsType === 'pitches' && (
                          <tr>
                            <th className="text-start" style={{ width: `${mobileNameWidth}%`, fontSize: '0.7rem', padding: '4px' }}>
                              {/* Home team pitch pager in header */}
                              {homePitchData.totalPitchPages > 1 ? (
                                <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                                  {Array.from({ length: homePitchData.totalPitchPages }, (_, index) => (
                                    <button
                                      key={index}
                                      onClick={() => goToHomePitch(index)}
                                      style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: index === homePitchIndex ? '#28a745' : '#dee2e6',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        padding: '0',
                                        margin: '0'
                                      }}
                                      aria-label={`Go to ${safeGame.home} pitch ${index + 1}`}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <span style={{ fontSize: '0.65rem', color: '#6c757d' }}>Player</span>
                              )}
                            </th>
                            {(() => {
                              // Group visible stats by pitch name
                              const pitchGroups = {};
                              visibleStats.forEach(stat => {
                                if (stat.pitchName) {
                                  if (!pitchGroups[stat.pitchName]) {
                                    pitchGroups[stat.pitchName] = [];
                                  }
                                  pitchGroups[stat.pitchName].push(stat);
                                }
                              });

                              return Object.entries(pitchGroups).map(([pitchName, stats]) => {
                                // For home team table, show away pitcher's percentage
                                let displayPitchName = pitchName;
                                if (stats[0]?.pitchData) {
                                  const { awayPitcher, homeSelectedPitchType } = stats[0].pitchData;
                                  if (awayPitcher) {
                                    const pitcherPitchData = awayPitcher.top4Pitches?.vsAll?.find(p => p.pitchType === homeSelectedPitchType);
                                    displayPitchName = formatPitchName(homeSelectedPitchType, pitcherPitchData);
                                  }
                                }

                                return (
                                  <th
                                    key={pitchName}
                                    colSpan={stats.length}
                                    style={{
                                      width: `${mobileStatWidth * stats.length}%`,
                                      textAlign: 'center',
                                      fontSize: '0.7rem',
                                      fontWeight: 'bold',
                                      backgroundColor: '#f8f9fa'
                                    }}
                                  >
                                    {displayPitchName}
                                  </th>
                                );
                              });
                            })()}
                          </tr>
                        )}

                        <tr>
                          <th
                            className="text-start player-name-cell"
                            style={{
                              width: `${mobileNameWidth}% !important`,
                              fontSize: '0.75rem',
                              color: 'yellow',
                              textTransform: 'none'
                            }}
                          >
                            {analyticsType === 'matchups' ? 'Player' :
                              analyticsType === 'performance' ? (
                                performanceView === 'last7' ? 'Last 7' :
                                performanceView === 'last14' ? 'Last 14' : 'Last 30'
                              ) : 'Pitch'}
                          </th>
                          {visibleStats.map(stat => (
                            <th key={stat.key} style={{ width: `${mobileStatWidth}%`, textAlign: 'center', fontSize: '0.7rem' }} title={stat.name}>
                              {stat.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {homeBatters.length === 0 && (
                          <tr>
                            <td colSpan={visibleStats.length + 1} className="text-center text-muted">
                              No home batters data
                            </td>
                          </tr>
                        )}
                        {homeBatters.map((row, index) => (
                          <tr key={index}>
                            <td
                              className="player-name-cell"
                              style={{
                                backgroundImage: `url(${getTeamLogoUrl(row.batterTeam || safeGame.home)})`,
                                width: `${mobileNameWidth}% !important`,
                                fontSize: '0.8rem',
                                cursor: 'pointer'
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const playerId = row.batterId || row.batterID || row.playerID || row.playerId || row.id;

                                if (onPlayerClick && playerId) {
                                  onPlayerClick(
                                    safeGame.gameIDmlb,
                                    playerId,
                                    row.batterName,
                                    null,
                                    safeGame.away,
                                    safeGame.home
                                  );
                                }
                              }}
                            >
                              <span>
                                {formatPlayerName ? formatPlayerName(row.batterName, true, false, 'mlb') : row.batterName}
                                {row.batterBats && (
                                  <span style={{ fontSize: '0.65rem', color: '#6c757d', marginLeft: '4px' }}>
                                    ({row.batterBats.charAt(0)})
                                  </span>
                                )}
                              </span>
                            </td>
                            {visibleStats.map(stat => (
                              <td
                                key={stat.key}
                                style={{
                                  ...getStatCellStyle(stat.key, row[stat.key]),
                                  width: `${mobileStatWidth}% !important`
                                }}
                              >
                                {formatValue(stat.key, row[stat.key], row)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {/* Last update timestamp */}
            {lastUpdate && (
              <div className="text-center mt-2">
                <small className="text-muted">
                  Last updated: {new Date(lastUpdate).toLocaleTimeString()}
                </small>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MobileAnalyticsDataTable;